// =============================================================================
// PRL / Documentos
// =============================================================================
//
// ⚠️ Este fichero describía un backend que NO EXISTE. Cuatro contratos
// inventados a la vez (auditoría ronda 2, bloque 11):
//
//  - la subida mandaba `file_base64` y `expires_at`; el endpoint pide
//    `expiry_date` y no acepta ficheros → 422 en cada intento del trabajador
//  - el estado leía `expires_at`, `days_until_expiry` y `status`; el backend
//    manda `expiry_date`, `days_remaining` y `overall_status` → el semáforo
//    decía "Sin caducidad" de lo que caduca
//  - la cola de validación leía `file_path`, `file_url`, `uploaded_at`; el
//    endpoint manda `document_type`, `expiry_date`, `file_uploaded` → la vista
//    previa reventaba con TypeError al seleccionar el primer documento
//  - `reject` no existía en todo el backend → el botón RECHAZAR siempre 404
//
// Los desajustes no petaban en ningún sitio: Pydantic descarta lo que no
// conoce y la PWA hace `catch`. Ahora los tipos son los del backend real.
//
// 📌 Y desde el 02/09/2026 los ficheros SÍ se guardan: van a un volumen
// nombrado en newton (nunca al bucket compartido, que sirve sin credencial) y
// los sirve la aplicación con su comprobación de empresa y de rol. El fichero
// es OPCIONAL: declarar el documento y su caducidad sigue valiendo, porque de
// la caducidad vive el radar.
import { apiClient } from './http';

export interface DocumentType {
  id: number;
  name: string;
  description: string | null;
  warning_days_1: number;
  warning_days_2: number;
  is_blocking: boolean;
  requires_verification: boolean;
  is_active: boolean;
}

/** Un documento en el semáforo del trabajador (GET /prl/status). */
export interface UserDocumentSummary {
  id: number;
  document_type_id: number;
  type_name: string;
  is_blocking: boolean;
  expiry_date: string | null;
  days_remaining: number | null;
  status: 'VALID' | 'WARNING_1' | 'WARNING_2' | 'EXPIRED' | 'PENDING';
  is_verified: boolean;
  file_uploaded: boolean;
}

export interface DocumentStatusResponse {
  user_id: number;
  overall_status: 'GREEN' | 'YELLOW' | 'RED';
  can_access_site: boolean;
  blocking_expired: string[];
  status_counts: Record<string, number>;
  documents: UserDocumentSummary[];
}

/** Lo que devuelve crear o verificar un documento (UserDocumentResponse). */
export interface UserDocument {
  id: number;
  document_type_id: number;
  document_type_name: string;
  expiry_date: string;
  days_remaining: number;
  status: 'VALID' | 'WARNING_1' | 'WARNING_2' | 'EXPIRED' | 'PENDING';
  is_verified: boolean;
  file_uploaded: boolean;
  notes: string | null;
  created_at: string;
  verified_at: string | null;
}

export async function getDocumentTypes(): Promise<DocumentType[]> {
  const response = await apiClient.get<DocumentType[]>('/prl/document-types');
  return response.data;
}

export async function getDocumentStatus(userId?: number): Promise<DocumentStatusResponse> {
  const url = userId ? `/prl/status/${userId}` : '/prl/status';
  const response = await apiClient.get<DocumentStatusResponse>(url);
  return response.data;
}

export interface DocumentUpload {
  document_type_id: number;
  /** Obligatoria: es el dato del que vive todo el radar de caducidades. */
  expiry_date: string;
  notes?: string;
  /** JPG, PNG o PDF en base64. Opcional: el radar vive de la caducidad. */
  file_base64?: string;
}

/** La URL del fichero de un documento. La sirve la API, con sesión. */
export function documentFileUrl(documentId: number): string {
  return `/api/v1/prl/documents/${documentId}/file`;
}

/** El fichero, como Blob: pasa por el interceptor y lleva el token. */
export async function getDocumentFile(documentId: number): Promise<Blob> {
  const res = await apiClient.get(`/prl/documents/${documentId}/file`, {
    responseType: 'blob',
  });
  return res.data as Blob;
}

export async function uploadDocument(data: DocumentUpload): Promise<UserDocument> {
  const response = await apiClient.post<UserDocument>('/prl/documents', data);
  return response.data;
}

// =============================================================================
// Admin PRL — validación de documentos
// =============================================================================

export interface PendingDocument {
  id: number;
  user_id: number;
  user_name: string;
  employee_code: string | null;
  document_type: string;
  is_blocking: boolean;
  expiry_date: string;
  file_uploaded: boolean;
  created_at: string;
}

export interface DocumentVerification {
  document_id: number;
  /** La caducidad que RRHH lee en el papel manda sobre la que tecleó quien lo subió. */
  expires_at?: string;
  notes?: string;
}

export interface DocumentRejection {
  document_id: number;
  reason: string;
}

export async function getPendingDocuments(): Promise<PendingDocument[]> {
  const response = await apiClient.get<PendingDocument[]>('/prl/documents/pending');
  return response.data;
}

export async function verifyDocument(data: DocumentVerification): Promise<void> {
  await apiClient.put(`/prl/documents/${data.document_id}/verify`, {
    is_verified: true,
    expires_at: data.expires_at,
    notes: data.notes,
  });
}

export async function rejectDocument(data: DocumentRejection): Promise<void> {
  await apiClient.post(`/prl/documents/${data.document_id}/reject`, {
    reason: data.reason,
  });
}

export async function deleteDocument(documentId: number): Promise<void> {
  await apiClient.delete(`/prl/documents/${documentId}`);
}
